import React from 'react';
import styled from '@emotion/styled';
import { Parallax } from 'react-scroll-parallax';
import { mq } from 'style/variables';

type CareerDetailParallaxBackgroundProps = {
  fvImages: Array<{
    url: string;
    url_org?: string;
    desc?: string;
  }>;
  fvSpImage: {
    url: string;
    url_org?: string;
    desc?: string;
  } | null;
  imageAspectRatios: string[];
};

// PC用のParallax背景
const ParallaxBackground = styled.div<{ bgImage: string; aspectRatio: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  aspect-ratio: ${props => props.aspectRatio};
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: top left;
  background-repeat: no-repeat;

  ${mq.onlysp} {
    display: none;
  }
`;

// SP用の固定背景
const FixedBackground = styled.div<{ bgImage: string }>`
  display: none;

  ${mq.onlysp} {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 375px;
    background-image: url(${props => props.bgImage});
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
  }
`;

// 視差効果の計算（1枚以下の場合は固定、複数枚の場合は視差をつける）
const getParallaxTranslateY = (index: number, totalImages: number): [string, string] => {
  // 画像が1枚以下の場合は固定（0枚や1枚の場合、除算エラーを防ぐ）
  if (totalImages <= 1) {
    return ['0px', '0px'];
  }
  
  // 複数枚の場合：奥のレイヤー（index=0）ほど大きく動く
  const maxMovement = 500;
  const minMovement = 0;
  const movement = maxMovement - (index * (maxMovement - minMovement) / (totalImages - 1));
  return ['0px', `${movement}px`];
};

export const CareerDetailParallaxBackground: React.FC<CareerDetailParallaxBackgroundProps> = ({
  fvImages,
  fvSpImage,
  imageAspectRatios,
}) => {
  const hasImages = fvImages.length > 0 || fvSpImage !== null;
  
  if (!hasImages) {
    return null;
  }

  return (
    <>
      {/* PC用: Parallax背景 */}
      {fvImages.length > 0 && fvImages.map((image, index) => (
        <Parallax
          key={`pc-${index}`}
          translateY={getParallaxTranslateY(index, fvImages.length)}
          startScroll={0}
          endScroll={1500}
          shouldAlwaysCompleteAnimation={true}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '2000px' }}
        >
          <ParallaxBackground 
            bgImage={image.url_org || image.url} 
            aspectRatio={imageAspectRatios[index] || '16 / 9'} 
          />
        </Parallax>
      ))}
      
      {/* SP用: 固定背景 */}
      {fvSpImage && (
        <FixedBackground 
          bgImage={fvSpImage.url_org || fvSpImage.url}
        />
      )}
    </>
  );
};

